"use client";

// useAvatarAnchor — publish the header avatar's CENTRE, measured from the
// viewport's top-right corner, as `--os-avatar-dx` / `--os-avatar-dy` on :root.
//
// Why this exists: on the `you` space netti rides up to the top-right and settles
// *over your avatar* — the page is about you, so the organism sits where your own
// face already is. OrganismBackdrop places the organism's centre at
// anchor + offset, and the organism's centre IS its central cell, so seating the
// cell on the avatar is purely a matter of knowing where the avatar is.
//
// That can't be a constant. The avatar is 38px on a phone and 48px on a desktop,
// the gutter is a clamp() that moves with the viewport, and the desktop shell
// insets its header under the traffic lights — three independent reasons a
// hard-coded offset would drift off the face on someone else's screen. So we
// measure the element and let CSS do the rest, the same way useBandHeight turns
// the navigator's edge into mask geometry.
//
// Sign convention matches the "top-right" anchor (left:100%, top:0): dx is
// negative (leftwards from the right edge), dy positive (downwards from the top).

import { useEffect, type RefObject } from "react";

const VAR_X = "--os-avatar-dx";
const VAR_Y = "--os-avatar-dy";

export function useAvatarAnchor(ref: RefObject<HTMLElement | null>, active = true): void {
  useEffect(() => {
    const root = document.documentElement;
    if (!active) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      // Nothing to trust yet: the avatar is hidden (the menu is open), or the
      // viewport hasn't been sized. Measuring against a zero-width viewport
      // yields a POSITIVE dx, which would fling netti off the right edge — so
      // hold the last good value and wait for the resize.
      if (r.width === 0 || window.innerWidth === 0) return;
      root.style.setProperty(VAR_X, `${Math.round(r.left + r.width / 2 - window.innerWidth)}px`);
      root.style.setProperty(VAR_Y, `${Math.round(r.top + r.height / 2)}px`);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(measure); };
    measure();
    window.addEventListener("resize", schedule);
    // Observe the ROOT, not just the avatar. The avatar's own box is a fixed
    // 38/48px and never changes when the window does, so an observer on it alone
    // fires once at mount and then never again — and the `resize` event isn't
    // dependable everywhere (an embedded/preview viewport can change without
    // emitting one). The document element's box tracks the viewport, so this is
    // the signal that actually arrives.
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    if (ro) {
      ro.observe(document.documentElement);
      if (ref.current) ro.observe(ref.current);
    }
    // fonts settle after first paint and can nudge the header's height
    if (typeof document !== "undefined" && "fonts" in document) {
      void (document as Document & { fonts: FontFaceSet }).fonts.ready.then(schedule).catch(() => {});
    }
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("resize", schedule);
      ro?.disconnect();
    };
  }, [ref, active]);
}
